import { Box, Button, ButtonProps, Menu, MenuItem } from '@mui/material';
import { useRef, useState } from 'react';
import { OptionLabel } from './option-label';
import { IconRenderer } from '../utils/render-icon';

type AllowedOptionValues = string | number | boolean;

export type TSelectOption<TValue extends AllowedOptionValues = AllowedOptionValues> = {
  value: TValue;
  label: React.ReactNode;
  icon?: React.ReactNode;
  hidden?: boolean;
  disabled?: boolean;
};

type Props<TValue extends AllowedOptionValues = AllowedOptionValues> = {
  value: TValue;
  onChange?: (value: TSelectOption<TValue> | null) => void;
  disabled?: boolean;
  options?: TSelectOption<TValue>[];
  triggerProps?: ButtonProps;
  menuWidth?: string;
  disabledIcon?: React.ReactNode;
  emptyValueLabel?: React.ReactNode; // label that displays when no value selected
  asTrigger?: boolean; // only renders trigger without menu
  helperIcon?: React.ReactNode;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (value: boolean) => void;
};

// NOTE: This component initially developed for TB-5371 (Note Scheduler)

export function Select<TValue extends AllowedOptionValues = AllowedOptionValues>({
  options,
  value,
  onChange,
  triggerProps,
  menuWidth,
  disabled,
  disabledIcon,
  emptyValueLabel,
  asTrigger,
  helperIcon,
  trigger,
  open: externalOpen,
  onOpenChange,
}: Props<TValue>) {
  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const selectedOption = options.find((item) => item.value === value);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const open = externalOpen ?? internalOpen;
  const setOpen = (isOpen: boolean) => {
    setInternalOpen(isOpen);
    onOpenChange?.(isOpen);
  };

  let selectIcon;

  if (disabled && !!disabledIcon) {
    selectIcon = <IconRenderer icon={disabledIcon} />;
  } else if (helperIcon) {
    selectIcon = <IconRenderer icon={helperIcon} />;
  } else {
    selectIcon = (
      <i
        className="fa-regular fa-chevron-down"
        style={{
          fontSize: '16px',
          color: '#667085',
          transform: open ? 'rotate(180deg)' : undefined,
          transition: 'transform 0.3s ease-in-out',
        }}
      />
    );
  }

  return (
    <>
      {trigger ? (
        <Box ref={anchorRef}>{trigger}</Box>
      ) : (
        <Button
          {...triggerProps}
          disabled={disabled}
          disableRipple
          ref={anchorRef}
          sx={{
            opacity: disabled ? '.5' : '1',
            transition: 'opacity 0.3s ease-in-out',
            p: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            borderRadius: '8px',
            border: '1px solid var(--Gray-300, #D0D5DD) !important',
            background: 'var(--White, #FFF) !important',
            boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
            color: '#344054',
            '&:hover': {
              color: '#344054',
            },
            ...triggerProps?.sx,
          }}
          onClick={(e) => {
            triggerProps?.onClick?.(e);
            setOpen(!open);
          }}
        >
          {selectedOption?.icon && (
            <Box
              sx={{ minWidth: 0, alignItems: 'center', display: 'flex', justifyContent: 'center' }}
            >
              <IconRenderer icon={selectedOption.icon} />
            </Box>
          )}

          <OptionLabel>
            {selectedOption ? selectedOption.label : value || (emptyValueLabel ?? 'Choose')}
          </OptionLabel>

          {selectIcon}
        </Button>
      )}

      <Menu
        elevation={0}
        open={asTrigger ? false : open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            '& .MuiMenu-list': {
              padding: 0,
            },
            mt: 1, // margin-top to move it down further
            background: 'var(--Gray-50, #F9FAFB) !important',
            borderRadius: '12px',
            border: '1px solid var(--Gray-300, #D0D5DD)',
            boxShadow: `0px 0px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)`,
            width: menuWidth ?? '200px',
            overflow: 'auto',
            maxHeight: '300px',
            '&::-webkit-scrollbar': {
              width: '16px',
              backgroundColor: '#fff !important',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
              borderRadius: '8px',
              margin: '4px 0',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#98A2B3',
              borderRadius: '8px',
              border: '6px solid transparent',
              backgroundClip: 'padding-box',
              minHeight: '30px',
            },
            '&::-webkit-scrollbar-thumb:active': {
              backgroundColor: '#98A2B3', // or theme.palette.grey[500]
            },
          },
        }}
      >
        {options
          .filter((option) => {
            return !option.hidden;
          })
          .map((option) => (
            <MenuItem
              disableRipple
              disabled={option.disabled}
              sx={{
                p: '10px',
                display: 'flex',
                gap: '10px',
                pointerEvents: option.disabled ? 'none' : 'auto',
                opacity: option.disabled ? '.5' : '1',
                justifyContent: 'flex-start',
                '&.Mui-selected': {
                  backgroundColor: '#F2F5FF !important',
                  '&:hover': {
                    backgroundColor: '#F2F5FF',
                    color: '#000',
                  },
                },
                '&:hover': {
                  backgroundColor: '#F2F5FF',
                  color: '#000',
                },
                '&.Mui-focusVisible': {
                  backgroundColor: '#F2F5FF',
                  color: '#000',
                },
              }}
              key={option.value.toString()}
              selected={option.value === value}
              onClick={() => {
                onChange?.(option);
                setOpen(false);
              }}
            >
              {option.icon && (
                <Box
                  sx={{
                    minWidth: 0,
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <IconRenderer icon={option.icon} />
                </Box>
              )}

              <OptionLabel>{option.label}</OptionLabel>
            </MenuItem>
          ))}
      </Menu>
    </>
  );
}
